import { useState, useRef, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from '../../hooks/redux'
import { getVoices, getTtsConfig } from '../../store/slices/tts/async'
import { getVoiceGroups, updateVoiceGroup, updateVoice, uploadVoiceAvatar, uploadVoiceSample, deleteVoiceAvatar } from '../../store/slices/voiceGroups/async'
import { addNotify } from '../../store/slices/notify/notifySlice'
import { useInputSanitize, DISPLAY_NAME_PATTERN } from '../../hooks/useInputSanitize'
import { useMediaUrl } from '../../hooks/useMediaUrl'
import { CustomButton } from '../../components/customButton'
import { CustomIcon } from '../../components/customIcon/customIcon'
import { TextField } from '../../components/textField/textField'
import { CustomSelect } from '../../components/customSelect'
import { OutClick } from '../../components/outClick'
import { Checkbox } from '../../components/checkbox/checkbox'
import { VoiceCard } from './voiceCard'
import { EmotionSelect } from './emotionSelect'
import { EmotionListConfig } from '../tts/config'
import { PortalTooltip } from '../../components/tooltip/portalTooltip'
import classes from '../../styles/voices/voicesTab.module.scss'
import adminClasses from '../../styles/admin/adminModal.module.scss'
import cn from 'classnames'

type TGender = 'male' | 'female' | null
type TEmotionItem = (typeof EmotionListConfig)[number]

type TVoiceGroupOption = { name: string; value: string; icon?: 'crown' | 'ttsLogo'; required?: boolean }

const LANG_NAMES: Record<string, string> = {
  ru: 'Русский',
  ru_stress: 'Русский (ударения)',
  en: 'Английский',
  kk: 'Казахский',
  kz: 'Казахский',
  tt: 'Татарский',
  az: 'Азербайджанский',
  uz: 'Узбекский',
  de: 'Немецкий',
  fr: 'Французский',
  es: 'Испанский',
  it: 'Итальянский',
  zh: 'Китайский',
}


export const VoicesTab = () => {
  const dispatch = useDispatch()
  const voices = useSelector((state) => state.tts.voices)
  const voiceLanguages = useSelector((state) => state.tts.voiceLanguages)
  const ttsConfig = useSelector((state) => state.tts.ttsConfig)
  const groups = useSelector((state) => state.voiceGroups.groups)

  const requiredGroups: TVoiceGroupOption[] = [...groups.filter((g) => g.is_system)]
    .reverse()
    .map((g) => ({ name: g.name, value: String(g.voice_group_id), icon: (g.voice_group_id === 69 ? 'crown' : 'ttsLogo') as 'crown' | 'ttsLogo', required: true }))

  const customGroups: TVoiceGroupOption[] = groups
    .filter((g) => !g.is_system)
    .map((g) => ({ name: g.name, value: String(g.voice_group_id) }))

  const allVoiceGroups = [...requiredGroups, ...customGroups]

  useEffect(() => {
    if (voices.length === 0) dispatch(getVoices() as any)
    if (groups.length === 0) dispatch(getVoiceGroups() as any)
    if (voiceLanguages.length === 0) dispatch(getTtsConfig() as any)
  }, [dispatch, voices.length, groups.length, voiceLanguages.length])

  const [gender, setGender] = useState<TGender>(null)
  const { value: displayName, onChange: onDisplayNameChange, onBlur: onDisplayNameBlur, reset: resetDisplayName } = useInputSanitize('', { maxLength: 50, allowPattern: DISPLAY_NAME_PATTERN })
  const { value: freeField, onChange: onFreeFieldChange, onBlur: onFreeFieldBlur, reset: resetFreeField } = useInputSanitize('', { maxLength: 200 })
  const [voiceLang, setVoiceLang] = useState<{ name: string; value: string | number }>({ name: 'Язык', value: '' })
  const [model, setModel] = useState<{ name: string; value: string | number }>({ name: 'Модель голоса', value: '' })
const [emotions, setEmotions] = useState<TEmotionItem[]>([])
  const [emotionNames, setEmotionNames] = useState<Record<string, string>>({})
  const [defaultEmotionName, setDefaultEmotionName] = useState<string | null>(null)
  const [avatar, setAvatar] = useState<string | null>(null)
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarRemoved, setAvatarRemoved] = useState(false)
  const [voiceAvatarPath, setVoiceAvatarPath] = useState<string | null>(null)
  const remoteVoiceAvatar = useMediaUrl(voiceAvatarPath)
  const [avatarError, setAvatarError] = useState<string | null>(null)
  const [audioFileName, setAudioFileName] = useState<string | null>(null)
  const [audioError, setAudioError] = useState<string | null>(null)
  const [localAudioUrl, setLocalAudioUrl] = useState<string | null>(null)
  const [voiceAudioPath, setVoiceAudioPath] = useState<string | null>(null)
  const remoteVoiceAudio = useMediaUrl(voiceAudioPath)
  const [voiceGroupOpen, setVoiceGroupOpen] = useState(false)
  const [selectedVoiceGroups, setSelectedVoiceGroups] = useState<string[]>(['69'])
  const [initialVoiceGroups, setInitialVoiceGroups] = useState<string[]>([])
  const [emotionResetKey, setEmotionResetKey] = useState(0)
  const [selectedVoiceName, setSelectedVoiceName] = useState<string | null>(null)
  const avatarInputRef = useRef<HTMLInputElement>(null)
  const audioInputRef = useRef<HTMLInputElement>(null)

  const loadVoice = (voice: typeof voices[number]) => {
    setSelectedVoiceName(voice.name)
    setGender(voice.sex || null)
    resetDisplayName()
    onDisplayNameChange({ target: { value: voice.title || voice.name } } as React.ChangeEvent<HTMLInputElement>)
    const lang = voice.languages?.[0] ?? ''
    setVoiceLang(lang ? { name: LANG_NAMES[lang] ?? lang, value: lang } : { name: 'Язык', value: '' })
    setModel({ name: voice.name, value: voice.name })
    const voiceEmotions = EmotionListConfig.filter((e) => voice.availableEmotions.includes(e.name))
    setEmotions(voiceEmotions)
    setDefaultEmotionName(voice.defaultEmotion ?? null)
    const voiceGroups = groups
      .filter((g) => g.voices.some((v) => v.technical_name === voice.name))
      .map((g) => String(g.voice_group_id))
    const voiceGroupsWithAll = voiceGroups.includes('69') ? voiceGroups : [...voiceGroups, '69']
    setSelectedVoiceGroups(voiceGroupsWithAll)
    setInitialVoiceGroups(voiceGroupsWithAll)
    if (voice.tooltip) onFreeFieldChange({ target: { value: voice.tooltip } } as React.ChangeEvent<HTMLTextAreaElement>)
    else resetFreeField()
    setEmotionResetKey((k) => k + 1)
    setAvatar(null)
    setAvatarFile(null)
    setAvatarRemoved(false)
    setVoiceAvatarPath(voice.avatar ?? null)
    setLocalAudioUrl(null)
    setAudioFileName(null)
    setAudioError(null)
    setVoiceAudioPath(voice.previewAudio ?? null)
    if (audioInputRef.current) audioInputRef.current.value = ''
  }

  const loadVoiceByName = (name: string | number) => {
    const voice = voices.find((v) => v.name === name)
    if (voice) loadVoice(voice)
  }

  const handleCancel = () => {
    setGender(null)
    resetDisplayName()
    resetFreeField()
    setVoiceLang({ name: 'Язык', value: '' })
    setModel({ name: 'Модель голоса', value: '' })
    setEmotions([])
    setDefaultEmotionName(null)
    setAvatar(null)
    setAvatarFile(null)
    setAvatarRemoved(false)
    setVoiceAvatarPath(null)
    setVoiceAudioPath(null)
    setAudioFileName(null)
    setLocalAudioUrl(null)
    setSelectedVoiceGroups(['all'])
    setVoiceGroupOpen(false)
    setEmotionResetKey((k) => k + 1)
    if (avatarInputRef.current) avatarInputRef.current.value = ''
    if (audioInputRef.current) audioInputRef.current.value = ''
  }

  const toggleVoiceGroup = (value: string) => {
    if (value === '69') return
    setSelectedVoiceGroups((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    )
  }

  const voiceGroupLabel = (() => {
    if (selectedVoiceGroups.length === 0) return 'Все голоса'
    return allVoiceGroups
      .filter((g) => selectedVoiceGroups.includes(g.value))
      .map((g) => g.name)
      .join(', ')
  })()

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.type !== 'image/webp') {
      setAvatarError('Формат должен быть WebP')
      e.target.value = ''
      return
    }
    if (file.size > 50 * 1024) {
      setAvatarError('Максимальный размер файла — 50 КБ')
      e.target.value = ''
      return
    }

    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      if (img.width !== 255 || img.height !== 180) {
        setAvatarError(`Разрешение должно быть 255×180 пикселей (загружено ${img.width}×${img.height})`)
        URL.revokeObjectURL(url)
        e.target.value = ''
        return
      }
      setAvatarError(null)
      setAvatarFile(file)
      setAvatar(url)
    }
    img.src = url
  }

  const handleAvatarRemove = (e: React.MouseEvent) => {
    e.stopPropagation()
    setAvatar(null)
    setAvatarFile(null)
    setAvatarRemoved(true)
    setVoiceAvatarPath(null)
    setAvatarError(null)
    if (avatarInputRef.current) avatarInputRef.current.value = ''
  }

  const handleAudioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.name.toLowerCase().endsWith('.wav')) {
      setAudioError('Формат должен быть WAV')
      e.target.value = ''
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      setAudioError('Максимальный размер файла — 2 МБ')
      e.target.value = ''
      return
    }

    const audioUrl = URL.createObjectURL(file)
    const audio = new Audio(audioUrl)
    audio.onloadedmetadata = () => {
      if (audio.duration > 30) {
        setAudioError('Максимальная длительность — 30 секунд')
        URL.revokeObjectURL(audioUrl)
        e.target.value = ''
        return
      }
      setAudioError(null)
      setAudioFileName(file.name)
      setLocalAudioUrl(audioUrl)
    }
    audio.src = audioUrl
  }

  const handleSave = async () => {
    if (!selectedVoiceName) return

    try {
      if (avatarRemoved) {
        await dispatch(deleteVoiceAvatar(selectedVoiceName) as any)
      }

      if (avatarFile) {
        await dispatch(uploadVoiceAvatar({ technical_name: selectedVoiceName, file: avatarFile }) as any)
      }

      const voiceRes = await dispatch(updateVoice({
        technical_name: selectedVoiceName,
        display_name: displayName || undefined,
        language_code: voiceLang.value ? String(voiceLang.value) : undefined,
        language_name: voiceLang.value ? LANG_NAMES[String(voiceLang.value)] : undefined,
        gender: gender ?? undefined,
        disclaimer: freeField || undefined,
        emotions: emotions.length > 0 ? emotions.map((e) => ({ technical_name: e.name, display_name: emotionNames[e.name] ?? e.tittle ?? '', emoji: e.emoji, is_default: e.name === defaultEmotionName })) : undefined,
      }) as any)

      if (voiceRes?.payload?.response_code !== 0) throw new Error()

      const added = selectedVoiceGroups.filter((id) => !initialVoiceGroups.includes(id))
      const removed = initialVoiceGroups.filter((id) => !selectedVoiceGroups.includes(id))

      for (const groupId of added) {
        const group = groups.find((g) => String(g.voice_group_id) === groupId)
        if (!group) continue
        const updatedVoices = Array.from(new Set([...group.voices.map((v) => v.technical_name), selectedVoiceName]))
        await dispatch(updateVoiceGroup({ voice_group_id: group.voice_group_id, voices: updatedVoices }) as any)
      }

      for (const groupId of removed) {
        const group = groups.find((g) => String(g.voice_group_id) === groupId)
        if (!group) continue
        const updatedVoices = group.voices.map((v) => v.technical_name).filter((n) => n !== selectedVoiceName)
        await dispatch(updateVoiceGroup({ voice_group_id: group.voice_group_id, voices: updatedVoices }) as any)
      }

      const audioFile = audioInputRef.current?.files?.[0]
      if (audioFile) {
        await dispatch(uploadVoiceSample({ technical_name: selectedVoiceName, file: audioFile }) as any)
      }

      await Promise.all([dispatch(getVoices() as any), dispatch(getTtsConfig() as any)])
      dispatch(addNotify({ text: 'Голос успешно сохранён' }))
    } catch {
      dispatch(addNotify({ text: 'Не удалось сохранить голос' }))
    }
  }

  return (
    <div className={classes.voicesTabWrapper}>
      <div className={classes.voicesTab}>
        <div className={classes.form}>
          <div className={classes.formTop}>
            <PortalTooltip
              placement="bottom-right"
              tooltipContent={avatarError ?? 'WebP, 255×180 пикселей, до 50 КБ'}
              classes={{ contentClassName: cn(classes.mediaTooltip, avatarError && classes.mediaTooltipError) }}
            >
              <div className={classes.avatarUpload}>
                {(avatar || remoteVoiceAvatar) ? (
                  <img src={avatar || remoteVoiceAvatar!} alt="avatar" className={classes.avatarImg} />
                ) : (
                  <CustomIcon icon="userIcon" />
                )}
                <div
                  className={cn(classes.avatarOverlay, avatarError && classes.avatarOverlayError)}
                  onClick={(avatar || remoteVoiceAvatar) ? handleAvatarRemove : () => avatarInputRef.current?.click()}
                >
                  <CustomIcon icon={(avatar || remoteVoiceAvatar) ? 'delete' : 'plus'} />
                </div>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/webp"
                  className={classes.hiddenInput}
                  onChange={handleAvatarChange}
                />
              </div>
            </PortalTooltip>

            <div className={classes.genderButtons}>
              <CustomButton
                className={cn(classes.genderBtn, gender === 'male' && classes.genderBtnActive)}
                onClick={() => setGender((g) => (g === 'male' ? null : 'male'))}
              >
                М
              </CustomButton>
              <CustomButton
                className={cn(classes.genderBtn, gender === 'female' && classes.genderBtnFemaleActive)}
                onClick={() => setGender((g) => (g === 'female' ? null : 'female'))}
              >
                Ж
              </CustomButton>
            </div>

            <PortalTooltip
              placement="bottom-middle"
              tooltipContent={audioError ?? (audioFileName ?? (remoteVoiceAudio ? (model.value as string || 'WAV, до 30 сек, до 2 МБ') : 'WAV, до 30 сек, до 2 МБ'))}
              classes={{ contentClassName: cn(classes.mediaTooltip, audioError && classes.mediaTooltipError), childrenWrapperClassName: classes.audioUploadWrapper }}
            >
              <button className={cn(classes.audioUpload, (audioFileName || remoteVoiceAudio) && classes.audioUploadActive, audioError && classes.audioUploadError)} onClick={() => audioInputRef.current?.click()}>
                <CustomIcon icon="download" />
                <span className={classes.audioUploadName}>{audioFileName ?? (remoteVoiceAudio ? (model.value as string || 'Пример голоса') : 'Пример голоса')}</span>
                {(audioFileName || remoteVoiceAudio) && (
                  <span
                    className={classes.audioUploadClear}
                    onClick={(e) => { e.stopPropagation(); setAudioFileName(null); setAudioError(null); setLocalAudioUrl(null); setVoiceAudioPath(null); if (audioInputRef.current) audioInputRef.current.value = '' }}
                  >
                    <CustomIcon icon="cross" />
                  </span>
                )}
                <input
                  ref={audioInputRef}
                  type="file"
                  accept=".wav"
                  className={classes.hiddenInput}
                  onChange={handleAudioChange}
                />
              </button>
            </PortalTooltip>
          </div>

          <div className={classes.formFields}>
            <CustomSelect
              items={voices.map((v) => ({ name: v.name, value: v.name }))}
              initialValue={model}
              handleChange={(item) => { setModel(item); loadVoiceByName(item.value) }}
              classes={{ outClickClassName: model.value ? classes.selectFilled : classes.selectPlaceholder, selectorWrapperClassName: classes.selectPadding }}
            />
            <CustomSelect
              items={voiceLanguages.map((lang) => ({ name: LANG_NAMES[lang] ?? lang, value: lang }))}
              initialValue={voiceLang}
              handleChange={setVoiceLang}
              classes={{ outClickClassName: voiceLang.value ? classes.selectFilled : classes.selectPlaceholder, selectorWrapperClassName: classes.selectPadding }}
            />
            <div className={classes.textFieldPadding}>
              <TextField
                localeText="Имя для отображения"
                value={displayName}
                type="text"
                handleChangeInput={onDisplayNameChange}
                onBlur={onDisplayNameBlur}
              />
            </div>
            <OutClick
              className={adminClasses.modalSelect}
              onClick={voiceGroupOpen ? () => setVoiceGroupOpen(false) : () => null}
            >
              <>
                <div
                  className={cn(adminClasses.voiceGroupTrigger, classes.voiceGroupTriggerPadding)}
                  onClick={() => setVoiceGroupOpen((p) => !p)}
                >
                  <span>{voiceGroupLabel}</span>
                  <CustomIcon icon={voiceGroupOpen ? 'arrowDown' : 'arrowRight'} />
                </div>
                {voiceGroupOpen && (
                  <div className={adminClasses.voiceGroupDropdown}>
                    {requiredGroups.map((group) => {
                      const count = groups.find((g) => String(g.voice_group_id) === group.value)?.voices.length ?? 0
                      return (
                        <div
                          key={group.value}
                          className={cn(adminClasses.voiceGroupItem, selectedVoiceGroups.includes(group.value) && adminClasses.voiceGroupItemSelected, group.value === '69' && adminClasses.voiceGroupItemDisabled)}
                          onClick={() => toggleVoiceGroup(group.value)}
                        >
                          <Checkbox
                            isChecked={selectedVoiceGroups.includes(group.value)}
                            handleCheckboxChange={() => {}}
                          />
                          {group.icon && (
                            <span className={group.icon === 'ttsLogo' ? adminClasses.voiceGroupIconMedium : adminClasses.voiceGroupIconSmall}>
                              <CustomIcon icon={group.icon} />
                            </span>
                          )}
                          <span className={cn(adminClasses.voiceGroupItemName, adminClasses.voiceGroupItemNameSystem)}>{group.name}</span>
                          <span className={adminClasses.voiceGroupItemCount}>
                            {count} {count === 1 ? 'голос' : count < 5 ? 'голоса' : 'голосов'}
                          </span>
                        </div>
                      )
                    })}
                    {requiredGroups.length > 0 && <div className={adminClasses.voiceGroupDivider} />}
                    {customGroups.length > 0 && (
                      <>
                        {customGroups.map((group) => (
                          <div
                            key={group.value}
                            className={cn(adminClasses.voiceGroupItem, selectedVoiceGroups.includes(group.value) && adminClasses.voiceGroupItemSelected)}
                            onClick={() => toggleVoiceGroup(group.value)}
                          >
                            <Checkbox
                              isChecked={selectedVoiceGroups.includes(group.value)}
                              handleCheckboxChange={() => {}}
                            />
                            <span className={adminClasses.voiceGroupItemName}>{group.name}</span>
                            <span className={adminClasses.voiceGroupItemCount}>
                              {(() => { const c = groups.find((g) => String(g.voice_group_id) === group.value)?.voices.length ?? 0; return `${c} ${c === 1 ? 'голос' : c < 5 ? 'голоса' : 'голосов'}` })()}
                            </span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </>
            </OutClick>
            <EmotionSelect
              value={emotions}
              onChange={(e, names, def) => { setEmotions(e); setEmotionNames(names); setDefaultEmotionName(def) }}
              resetKey={emotionResetKey}
              allowedEmotions={selectedVoiceName ? (ttsConfig.find((c) => c.voice === selectedVoiceName)?.emotions ?? null) : null}
              initialDefaultEmotion={defaultEmotionName}
            />
            <textarea
              className={classes.freeField}
              placeholder="Дисклеймер"
              value={freeField}
              onChange={onFreeFieldChange}
              onBlur={onFreeFieldBlur}
            />
          </div>
        </div>

        <div className={classes.grid}>
          {[...voices].sort((a, b) => a.name === selectedVoiceName ? -1 : b.name === selectedVoiceName ? 1 : 0).map((voice) => {
            const isSelected = voice.name === selectedVoiceName
            return (
              <VoiceCard
                key={voice.name}
                name={isSelected ? (displayName || voice.title || voice.name) : (voice.title || voice.name)}
                gender={isSelected ? (gender || undefined) : (voice.sex || undefined)}
                lang={isSelected ? (voiceLang.value as string || undefined) : voice.languages?.[0]}
                avatar={isSelected ? (avatar || (!avatarRemoved ? voice.avatar : undefined) || undefined) : (voice.avatar || undefined)}
                previewAudio={isSelected ? (localAudioUrl || voice.previewAudio || undefined) : (voice.previewAudio || undefined)}
                blur={!isSelected}
                onClick={() => loadVoice(voice)}
              />
            )
          })}
        </div>
      </div>
      <div className={classes.voicesFooter}>
        <CustomButton className={classes.cancelBtn} onClick={handleCancel}>Отменить</CustomButton>
        <CustomButton viewType="primary" className={classes.saveBtn} onClick={handleSave}>
          Сохранить
        </CustomButton>
      </div>
    </div>
  )
}
